import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Button, DataTable, SelectField } from "components";
import {
  useGetAllCustomers,
  useGetNinjaMappedCustomers,
  useGetNinjaOneCustomers,
  useMapNinjaOneCustomer,
} from "controller";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import toast from "react-hot-toast";
import { useDebounce } from "utilz/helpers/hooks/useDebounceHook";

const CUSTOMER_PAGE_SIZE = 100;

type NinjaOneCustomer = {
  id: number;
  name: string;
  [key: string]: unknown;
};

type MappedCustomerRecord = {
  external_customer_id: number | string;
  deskday_customer_id: string;
  deskday_customer_name: string;
  [key: string]: unknown;
};

type DeskdayCustomer = {
  mapping_id: string;
  customer_name: string;
  [key: string]: unknown;
};

type CustomerMappingRow = {
  deskday_customer_id: string;
  deskday_customer_name: string;
  external_customer_id: string;
  external_customer_name: string;
};

const TableHeaders = [
  {
    name: "NinjaOne RMM Customer",
    sortable: false,
    key: "external_customer_name",
  },
  {
    name: "DeskDay Customer",
    sortable: false,
    key: "ddCustomer",
  },
];

export const NinjaOneStep1 = forwardRef((_props, ref) => {
    const theme = useTheme();

    const { mutateAsync: mapNinjaCustomer } = useMapNinjaOneCustomer();

    // Debounced server-side search — keeps customers beyond the first page
    // reachable instead of filtering only the loaded page.
    const [customerSearch, setCustomerSearch] = useDebounce("", 400);
    const { data: customer, isFetching: isCustomersFetching } =
      useGetAllCustomers({
        pageNumber: 1,
        perPageCount: 2000,
        sortColumn: "customer_name",
        sortDirection: "asc",
        filter: [{ column: "is_active", operator: "=", value: "true" }],
        searchQuery: customerSearch,
      });
    const {
      data: ninjaCustomersData,
      isLoading: isNinjaCustomerLoading,
      fetchNextPage: fetchNinjaCustomersNextPage,
      hasNextPage: hasNinjaCustomersNextPage,
      isFetchingNextPage: isFetchingNinjaCustomersNextPage,
    } = useGetNinjaOneCustomers(CUSTOMER_PAGE_SIZE);
    const {
      data: ninjaMappedCustomers,
      isLoading: isNinjaMappedCustomersLoading,
    } = useGetNinjaMappedCustomers("ninjaOne");
    const [transformedCustomerData, setTransformedCustomerData] = useState<
      CustomerMappingRow[]
    >([]);

    // The rows are built in the effects below, a render after the queries
    // settle. Keep the skeleton up until they exist.
    const isBuildingRows =
      Boolean(ninjaCustomersData?.pages?.some((page) => page?.data?.length)) &&
      Boolean(ninjaMappedCustomers) &&
      transformedCustomerData.length === 0;
    const [allNinjaCustomers, setAllNinjaCustomers] = useState<
      NinjaOneCustomer[]
    >([]);

    useEffect(() => {
      if (ninjaCustomersData?.pages) {
        const flat = ninjaCustomersData.pages.flatMap(
          (page) => page?.data || [],
        );
        setAllNinjaCustomers(flat);
      }
    }, [ninjaCustomersData]);

    useEffect(() => {
      if (allNinjaCustomers.length > 0 && ninjaMappedCustomers) {
        createMappingArray();
      }
    }, [allNinjaCustomers, ninjaMappedCustomers]);

    const createMappingArray = () => {
      setTransformedCustomerData((prev) => {
        // Preserve in-progress (unsaved) edits across Load More by keying
        // existing rows by external_customer_id and reusing them for any
        // fetched customer we already have an entry for.
        const existingById = new Map(
          prev.map((row) => [String(row.external_customer_id), row]),
        );
        return allNinjaCustomers.map((customer) => {
          const idStr = String(customer.id);
          const existing = existingById.get(idStr);
          if (existing) return existing;

          const customerIndex =
            ninjaMappedCustomers?.findIndex(
              (mappedCustomer: MappedCustomerRecord) =>
                String(mappedCustomer.external_customer_id) === idStr,
            ) ?? -1;
          return {
            deskday_customer_id:
              customerIndex > -1
                ? ninjaMappedCustomers[customerIndex].deskday_customer_id
                : "",
            deskday_customer_name:
              customerIndex > -1
                ? ninjaMappedCustomers[customerIndex].deskday_customer_name
                : "",
            external_customer_id: idStr,
            external_customer_name: customer.name,
          };
        });
      });
    };

    const addCustomerMapping = ({
      customerId,
      externalCustomerId,
    }: {
      customerId: string | null;
      externalCustomerId: string;
    }) => {
      if (!externalCustomerId) return;

      const matchedCustomer = customerId
        ? (customer?.result.find(
            (c: DeskdayCustomer) => c.mapping_id === customerId,
          ) as DeskdayCustomer | undefined)
        : undefined;

      setTransformedCustomerData((prev) => {
        const idx = prev.findIndex(
          (row) => row.external_customer_id === externalCustomerId,
        );
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          deskday_customer_id: matchedCustomer?.mapping_id ?? "",
          deskday_customer_name: matchedCustomer?.customer_name ?? "",
        };
        return next;
      });
    };
    const cellBuilders = {
      ddCustomer: (rowData: CustomerMappingRow) => {
        return (
          <SelectField
            key={rowData.external_customer_id}
            sx={{ width: "180px" }}
            name="customer"
            hideHelperBox
            options={customer?.result || []}
            isSearchable
            isLoading={isCustomersFetching}
            onSearchValueChange={setCustomerSearch}
            // Search is served by the backend (`searchQuery`), so `onFilterValue`
            // returns true to stop the client from re-filtering the results.
            onFilterValue={() => true}
            size="small"
            value={rowData.deskday_customer_id}
            isResponsive={false}
            labelMappingKey="customer_name"
            valueMappingKey="mapping_id"
            // The options list shrinks while a search is active, so fall back to
            // the name held in row state to keep every row's label stable.
            renderValue={(_value, optionValue) =>
              optionValue?.customer_name || rowData.deskday_customer_name || ""
            }
            searchFieldProps={{
              placeholder: "Search customer",
            }}
            onValueChange={(value) => {
              addCustomerMapping({
                customerId: value,
                externalCustomerId: rowData.external_customer_id,
              });
            }}
            isClearable
          />
        );
      },
    };

    const handleTransformForSubmit = async () => {
      const submitArray: Array<CustomerMappingRow> = [];
      transformedCustomerData?.forEach((customer) => {
        if (customer.deskday_customer_id) {
          submitArray.push({
            ...customer,
            external_customer_id: customer.external_customer_id,
            external_customer_name: customer.external_customer_name,
          });
        }
      });
      return submitArray;
    };

    const onSubmit = async () => {
      try {
        const mappedCustomers = await handleTransformForSubmit();
        await mapNinjaCustomer({
          customerData: mappedCustomers,
          source: "ninjaOne",
        });
        toast.success("Customer mapped successfully");
        return true;
      } catch {
        toast.error("Failed to map customers");
        return false;
      }
    };

    useImperativeHandle(ref, () => {
      return {
        onSubmit,
      };
    });
    return (
      <Stack
        sx={{ px: "32px", py: "24px", gap: "16px", height: "100%" }}
        position="relative"
      >
        <Typography variant="h6">Customer mapping</Typography>
        <Typography variant="body-medium" color={theme.palette.lightBg.low}>
          Please map your customers with corresponding customer names in
          NinjaOne RMM
        </Typography>
        <Box
          sx={{
            flex: "1",
            width: "100%",
            position: "relative",
          }}
        >
          <DataTable
            isDataLoading={
              isNinjaCustomerLoading ||
              isNinjaMappedCustomersLoading ||
              isBuildingRows
            }
            headers={TableHeaders || []}
            data={transformedCustomerData || []}
            uniqueKey="external_customer_id"
            cellBuilders={cellBuilders}
            selectedItems={[]}
            currentPageNumber={0}
            itemsPerPage={CUSTOMER_PAGE_SIZE}
            multiselect={false}
            containerStyles={{ height: "100%", minHeight: "200px" }}
            headerCellStyles={{
              textTransform: "none",
            }}
          />
          {hasNinjaCustomersNextPage && (
            <Stack
              justifyContent={"center"}
              alignItems={"center"}
              position="absolute"
              bottom="8px"
              left={"50%"}
              sx={{
                transform: "translateX(-50%)",
                zIndex: 1000,
              }}
            >
              <Button
                variant="ghost"
                onClick={() => fetchNinjaCustomersNextPage()}
                disabled={isFetchingNinjaCustomersNextPage}
                loading={isFetchingNinjaCustomersNextPage}
                sx={{
                  textTransform: "none",
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                {isFetchingNinjaCustomersNextPage
                  ? "Loading..."
                  : "Load More Customers"}
              </Button>
            </Stack>
          )}
        </Box>
      </Stack>
    );
  },
);
